import useAxios from "@hooks/useFetch";
import { components, paths } from "@api/api";
import { useMemo } from "react";
import { Metric } from "@models/Metric";

const ENDPOINT: keyof paths = '/api/Common/Get';
export type CommonDataResponse = components["schemas"]["FlavorTracker.Entities.DTO.ViewModels.CommonDataViewModel"];
export type CommonDataKeywordItem = components["schemas"]["FlavorTracker.Entities.DTO.Common.CommonKeyword"];
export type CommonDataResponseExtended= Omit<CommonDataResponse, 'keywords'> & {
    keywords?: Record<number, CommonDataKeywordItem>;
};

export type ExtendedKeywordItem = {
	name: CommonDataKeywordItem["name"];
	macStage: CommonDataKeywordItem["macStage"] | null;
	macStageTitle: string;
	heatScore: CommonDataKeywordItem["heatScore"];
	heatScoreTitle: string;
	heatScoreNum: number | null;
	subCategoriesIds: CommonDataKeywordItem["subCategoriesIds"];
	metrics: {
		id: Metric;
		name: string;
		value: string;
		numValue: number | null;
		changeValue: string;
		changeValueSigned: string;
		changeNumValue: number | null;
	}[];
};

interface IUseCommonData {
    data: CommonDataResponseExtended;
    isLoading: boolean;
}

export function useCommonData(): IUseCommonData {
    const { data: rawData, isLoading } = useAxios<CommonDataResponse>({
        url: ENDPOINT,
        method: 'get',
    });

    const categories = useMemo(
        () => {
            const categories = (rawData?.categories ?? []).map(i => {
                return {
                    ...i,
                    subCategories: (i.subCategories ?? []).map((subCategory) => ({
                        ...subCategory,
                        isEnabled: true,
                    })),
                };
            });

            categories.sort((a, b) => {
                return a.name?.localeCompare(b.name ?? '') ?? 0;
            });

            return categories;
        },
        [rawData],
    );

    const keywords = useMemo(
        () => {
            return Object.fromEntries(rawData?.keywords?.map(i => [i.id ?? 0, i]) ?? []);
        },
        [rawData]
    );

    const data = useMemo(
        () => ({
            ...rawData,
            categories,
            keywords,
        }),
        [rawData, categories, keywords]
    );

    return {
        data,
        isLoading,
    };
}